/**
 * Theme Switcher Component
 *
 * A toggle button for switching between light and dark themes.
 * Follows the Slate monochromatic design system.
 */

import { Moon, Sun } from 'lucide-react'
import { Button } from './ui/button'
import { useTheme } from '../providers/theme-provider'
import { useEffect, useState } from 'react'

/**
 * Theme Switcher Button
 *
 * Displays the current theme and allows toggling.
 * Shows a sun icon in dark mode (to switch to light)
 * Shows a moon icon in light mode (to switch to dark)
 *
 * @example
 * <ThemeSwitcher />
 */
export function ThemeSwitcher() {
  const { resolvedTheme, setMode } = useTheme()
  const [mounted, setMounted] = useState(false)

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    // Render placeholder during SSR to avoid hydration mismatch
    return (
      <Button variant="ghost" size="sm" className="w-9 px-0">
        <Sun className="h-4 w-4" />
        <span className="sr-only">Toggle theme</span>
      </Button>
    )
  }

  const isDark = resolvedTheme === 'dark'

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setMode(isDark ? 'light' : 'dark')}
      className="w-9 px-0"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <Sun className="h-4 w-4 transition-all" />
      ) : (
        <Moon className="h-4 w-4 transition-all" />
      )}
      <span className="sr-only">Toggle theme</span>
    </Button>
  )
}
